import type { FeedDto } from '@/libs/api/feed';

/**
 * 목록 → 상세 → 뒤로 가기 때 목록 상태(불러온 카드·커서·스크롤·초점 카드)를 되살리기 위한 저장소.
 * lone-web-pc pc-v2-handoff의 "뒤로 시 게시물 ID·스크롤 위치·초점 복원" 규칙을 따른다.
 *
 * 저장은 목록이 사라질 때 하고, 복원은 뒤로/앞으로 가기(popstate)로 같은 목록에 돌아왔을 때만 한다.
 * 새로 들어온 경우에는 저장본을 버려 오래된 목록이 보이지 않게 한다.
 */

const STORAGE_PREFIX = 'lone:feed-list:';
// popstate 뒤 새 화면이 마운트되기까지 서버 렌더를 기다릴 수 있어 여유를 둔다.
const HISTORY_NAVIGATION_WINDOW_MS = 10_000;
const SNAPSHOT_TTL_MS = 30 * 60 * 1000;
// 세션 저장소를 과하게 쓰지 않도록 복원할 카드 수에 상한을 둔다.
const MAX_SNAPSHOT_FEEDS = 200;

let lastPopStateAt = 0;

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    lastPopStateAt = Date.now();
  });
}

export const isRecentHistoryNavigation = () => Date.now() - lastPopStateAt < HISTORY_NAVIGATION_WINDOW_MS;

export interface FeedListSnapshot {
  feeds: FeedDto[];
  beforeCreatedAt?: string;
  page: number;
  hasMore: boolean;
  scrollY: number;
  feedId?: string;
  savedAt: number;
}

export const saveFeedListSnapshot = (key: string, snapshot: Omit<FeedListSnapshot, 'savedAt'>) => {
  try {
    const feeds = snapshot.feeds.slice(0, MAX_SNAPSHOT_FEEDS);
    const value: FeedListSnapshot = {
      ...snapshot,
      feeds,
      hasMore: feeds.length < snapshot.feeds.length ? false : snapshot.hasMore,
      savedAt: Date.now(),
    };
    window.sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // 저장소가 가득 찼거나 막혀 있으면 복원 없이 기존 동작을 따른다.
  }
};

/** 저장본을 꺼내며 지운다. 뒤로 가기로 돌아온 경우가 아니면 버린다. */
export const takeFeedListSnapshot = (key: string): FeedListSnapshot | undefined => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return undefined;
    window.sessionStorage.removeItem(STORAGE_PREFIX + key);
    if (!isRecentHistoryNavigation()) return undefined;

    const snapshot = JSON.parse(raw) as FeedListSnapshot;
    if (!Array.isArray(snapshot.feeds) || Date.now() - snapshot.savedAt > SNAPSHOT_TTL_MS) return undefined;
    return snapshot;
  } catch {
    return undefined;
  }
};
